import { FormattedMessage, useIntl } from 'react-intl';
import { FormTextField, FormControl, LoadingButton } from 'components/common';
import { FcaTextfield } from 'pages/SignupPage/NewAuthFormComponents/FcaTextfield';
import { useFormContext, useWatch } from 'react-hook-form';
import { SignupOrganisationFormFields } from '../signupOrganisationForm.types';
import { AddressFields } from '../AddressFields';
import { addressFieldPathsFor, isAddressManualPathFor, orgAddressValuePath } from '../addressFieldPaths';
import { brokerOrgFormRoot, formPath, networkField, orgField } from '../formFieldPaths';
import {
  rulesRequiredYesNo,
  rulesRequiredYesNoUnsure,
  yesNoBooleanRadioOptions,
  yesNoUnsureBooleanRadioOptions,
} from '../yesNoRadioOptions';

const textIn = {
  type: 'text' as const,
  inputProps: { sx: { padding: '6px !important' } as const, className: 'text-sm !font-normal' as const },
};

type NetworkFormProps = {
  isFcaLoading: boolean;
  onFcaFind: () => void;
  disabled: boolean;
  showFieldsAfterFca: boolean;
};

export function NetworkForm({ isFcaLoading, onFcaFind, disabled, showFieldsAfterFca }: NetworkFormProps) {
  const intl = useIntl();
  const { control } = useFormContext<SignupOrganisationFormFields>();
  const base = brokerOrgFormRoot.network;
  const networkBackendId = useWatch({ name: formPath(base, orgField.backendId), control });
  const locked = Boolean(networkBackendId);
  const required = intl.formatMessage({ id: 'common.error.requiredField' });
  const showRest = showFieldsAfterFca || locked;

  return (
    <div className='grid w-full max-w-4xl grid-cols-1 gap-4 border-t border-gray-200 pt-6 md:grid-cols-2'>
      <h3 className='md:col-span-2 text-base font-semibold'>
        <FormattedMessage id='auth.signup.linkOrganisationAR.title' />
      </h3>
      <div className='flex flex-col gap-2 sm:flex-row sm:items-end md:col-span-2'>
        <div className='min-w-0 grow'>
          <FcaTextfield name={formPath(base, orgField.financialConductAuthorityNumber)} />
        </div>
        {!isFcaLoading && !showFieldsAfterFca && (
          <LoadingButton
            isLoading={isFcaLoading}
            buttonProps={{
              type: 'button',
              variant: 'contained',
              className: 'h-9 shrink-0',
              onClick: onFcaFind,
              disabled: disabled || locked,
            }}
          >
            <FormattedMessage id='cta.continue' />
          </LoadingButton>
        )}
      </div>
      {showRest && (
        <>
          <div className='md:col-span-2'>
            <FormattedMessage id='auth.signup.companyName' />
            <FormTextField
              name={formPath(base, orgField.name)}
              rules={{ required: { value: true, message: required } }}
              disabled={disabled || locked}
              textFieldProps={{
                placeholder: intl.formatMessage({ id: 'placeholders.companyDetails.name' }),
                ...textIn,
              }}
            />
          </div>
          {!networkBackendId && (
            <div>
              <FormattedMessage id='auth.signup.createOrganisation.network.partOfMortgageClub' />
              <FormControl
                name={formPath(base, networkField.isPartOfMortgageClub)}
                rules={rulesRequiredYesNo(required)}
                columnsAmount={2}
                disabled={disabled}
                noPadding
                radioOptions={yesNoBooleanRadioOptions}
              />
            </div>
          )}
          <div>
            <FormattedMessage id='auth.signup.createOrganisation.network.isStrideUpOnPanel' />
            <FormControl
              name={formPath(base, networkField.isStrideUpOnPanel)}
              rules={rulesRequiredYesNoUnsure(required)}
              columnsAmount={3}
              disabled={disabled || locked}
              noPadding
              radioOptions={yesNoUnsureBooleanRadioOptions}
            />
          </div>
          <div className='md:col-span-2'>
            <div className='mb-1 text-sm font-medium'>Network address</div>
            <AddressFields
              fieldPaths={addressFieldPathsFor(base)}
              manualTogglePath={isAddressManualPathFor(base)}
              addressValuePath={orgAddressValuePath(base)}
              disabled={Boolean(disabled || locked)}
            />
          </div>
        </>
      )}
    </div>
  );
}
